import { Direction, GameState, Tile, SlideResult } from './types';
import { GRID_SIZE, WIN_VALUE, STORAGE_KEYS } from './constants';

let tileIdCounter = 0;

function generateTileId(): number {
  return ++tileIdCounter;
}

export function resetTileIdCounter(): void {
  tileIdCounter = 0;
}

// Create empty 4x4 grid
function createEmptyGrid(): (Tile | null)[][] {
  return Array(GRID_SIZE)
    .fill(null)
    .map(() => Array(GRID_SIZE).fill(null));
}

// Clone grid deeply
function cloneGrid(grid: (Tile | null)[][]): (Tile | null)[][] {
  return grid.map(row =>
    row.map(tile =>
      tile ? { ...tile, isNew: false, isMerged: false } : null
    )
  );
}

// Load best score from localStorage
export function loadBestScore(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.BEST_SCORE);
    return saved ? parseInt(saved, 10) : 0;
  } catch {
    return 0;
  }
}

// Save best score to localStorage
export function saveBestScore(score: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.BEST_SCORE, score.toString());
  } catch {
    // Ignore storage errors
  }
}

// Add a random tile to an empty cell
function addRandomTile(grid: (Tile | null)[][]): boolean {
  const emptyCells: { row: number; col: number }[] = [];

  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!grid[row][col]) {
        emptyCells.push({ row, col });
      }
    }
  }

  if (emptyCells.length === 0) return false;

  const { row, col } = emptyCells[Math.floor(Math.random() * emptyCells.length)];
  grid[row][col] = {
    id: generateTileId(),
    value: Math.random() < 0.9 ? 2 : 4,
    row,
    col,
    isNew: true
  };

  return true;
}

// Initialize a new game
export function initializeGame(seedTiles?: number[]): GameState {
  resetTileIdCounter();
  const grid = createEmptyGrid();

  // Development mode: seed board with specific tiles from query parameter
  if (seedTiles && seedTiles.length > 0) {
    let tileIndex = 0;
    for (let row = 0; row < GRID_SIZE && tileIndex < seedTiles.length; row++) {
      for (let col = 0; col < GRID_SIZE && tileIndex < seedTiles.length; col++) {
        const value = seedTiles[tileIndex];
        if (value > 0) {
          grid[row][col] = {
            id: generateTileId(),
            value,
            row,
            col,
            isNew: false
          };
        }
        tileIndex++;
      }
    }
  } else {
    // Normal mode: add two random tiles
    addRandomTile(grid);
    addRandomTile(grid);
  }

  // Check if game is already over (important for seeded boards)
  const gameOver = !hasValidMoves(grid);
  const won = hasValue(grid, WIN_VALUE);

  return {
    grid,
    score: 0,
    bestScore: loadBestScore(),
    gameOver,
    won,
    moveCount: 0
  };
}

// Get a row from the grid
function getRow(grid: (Tile | null)[][], rowIndex: number): (Tile | null)[] {
  return [...grid[rowIndex]];
}

// Set a row in the grid
function setRow(grid: (Tile | null)[][], rowIndex: number, tiles: (Tile | null)[]): void {
  for (let col = 0; col < GRID_SIZE; col++) {
    if (tiles[col]) {
      tiles[col]!.row = rowIndex;
      tiles[col]!.col = col;
    }
    grid[rowIndex][col] = tiles[col];
  }
}

// Get a column from the grid
function getColumn(grid: (Tile | null)[][], colIndex: number): (Tile | null)[] {
  return grid.map(row => row[colIndex]);
}

// Set a column in the grid
function setColumn(grid: (Tile | null)[][], colIndex: number, tiles: (Tile | null)[]): void {
  for (let row = 0; row < GRID_SIZE; row++) {
    if (tiles[row]) {
      tiles[row]!.row = row;
      tiles[row]!.col = colIndex;
    }
    grid[row][colIndex] = tiles[row];
  }
}

// Slide and merge a row/column towards index 0
function slideRow(tiles: (Tile | null)[]): SlideResult {
  // Filter out nulls
  const nonEmpty = tiles.filter((t): t is Tile => t !== null);

  const result: (Tile | null)[] = [];
  let score = 0;
  let i = 0;

  while (i < nonEmpty.length) {
    if (i + 1 < nonEmpty.length && nonEmpty[i].value === nonEmpty[i + 1].value) {
      // Merge tiles
      const mergedValue = nonEmpty[i].value * 2;
      result.push({
        id: generateTileId(),
        value: mergedValue,
        row: 0,
        col: 0,
        isMerged: true
      });
      score += mergedValue;
      i += 2;
    } else {
      result.push({ ...nonEmpty[i] });
      i++;
    }
  }

  // Pad with nulls
  while (result.length < GRID_SIZE) {
    result.push(null);
  }

  // Check if anything moved
  let moved = false;
  for (let j = 0; j < GRID_SIZE; j++) {
    const originalValue = tiles[j]?.value || 0;
    const newValue = result[j]?.value || 0;
    if (originalValue !== newValue) {
      moved = true;
      break;
    }
    // Check position change
    const originalId = tiles[j]?.id;
    const newId = result[j]?.id;
    if (originalId !== newId && (originalId || newId)) {
      moved = true;
      break;
    }
  }

  // Also check if non-null tiles shifted positions
  if (!moved) {
    const originalPositions = tiles.map((t, idx) => t ? idx : -1).filter(x => x >= 0);
    const newPositions = result.map((t, idx) => t ? idx : -1).filter(x => x >= 0);
    if (originalPositions.length !== newPositions.length) {
      moved = true;
    } else {
      for (let j = 0; j < originalPositions.length; j++) {
        if (originalPositions[j] !== newPositions[j]) {
          moved = true;
          break;
        }
      }
    }
  }

  return { tiles: result, moved, score };
}

// Check if grid has a specific value
function hasValue(grid: (Tile | null)[][], value: number): boolean {
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (grid[row][col]?.value === value) {
        return true;
      }
    }
  }
  return false;
}

// Check if any valid moves exist
function hasValidMoves(grid: (Tile | null)[][]): boolean {
  // Check for empty cells
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (!grid[row][col]) return true;
    }
  }

  // Check for adjacent matches
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const value = grid[row][col]!.value;
      // Check right
      if (col < GRID_SIZE - 1 && grid[row][col + 1]?.value === value) return true;
      // Check down
      if (row < GRID_SIZE - 1 && grid[row + 1][col]?.value === value) return true;
    }
  }

  return false;
}

// Main move function
export function move(state: GameState, direction: Direction): GameState {
  if (state.gameOver) return state;

  const newGrid = cloneGrid(state.grid);
  let moved = false;
  let scoreGained = 0;

  if (direction === 'left') {
    for (let row = 0; row < GRID_SIZE; row++) {
      const result = slideRow(getRow(newGrid, row));
      setRow(newGrid, row, result.tiles);
      moved = moved || result.moved;
      scoreGained += result.score;
    }
  } else if (direction === 'right') {
    for (let row = 0; row < GRID_SIZE; row++) {
      const rowTiles = getRow(newGrid, row).reverse();
      const result = slideRow(rowTiles);
      setRow(newGrid, row, result.tiles.reverse());
      moved = moved || result.moved;
      scoreGained += result.score;
    }
  } else if (direction === 'up') {
    for (let col = 0; col < GRID_SIZE; col++) {
      const result = slideRow(getColumn(newGrid, col));
      setColumn(newGrid, col, result.tiles);
      moved = moved || result.moved;
      scoreGained += result.score;
    }
  } else if (direction === 'down') {
    for (let col = 0; col < GRID_SIZE; col++) {
      const colTiles = getColumn(newGrid, col).reverse();
      const result = slideRow(colTiles);
      setColumn(newGrid, col, result.tiles.reverse());
      moved = moved || result.moved;
      scoreGained += result.score;
    }
  }

  if (!moved) return state;

  // Add new random tile
  addRandomTile(newGrid);

  // Check for win
  const won = state.won || hasValue(newGrid, WIN_VALUE);

  // Check for game over (either no valid moves OR just won)
  const gameOver = !hasValidMoves(newGrid) || (won && !state.won);

  const newScore = state.score + scoreGained;
  const bestScore = Math.max(newScore, state.bestScore);

  // Save best score
  if (bestScore > state.bestScore) {
    saveBestScore(bestScore);
  }

  return {
    grid: newGrid,
    score: newScore,
    bestScore,
    gameOver,
    won,
    moveCount: state.moveCount + 1
  };
}

// Get the highest tile value on the grid
export function getHighestTileValue(grid: (Tile | null)[][]): number {
  let highest = 0;
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const value = grid[row][col]?.value || 0;
      if (value > highest) {
        highest = value;
      }
    }
  }
  return highest;
}

// Get all tiles as flat array (for rendering)
export function getAllTiles(grid: (Tile | null)[][]): Tile[] {
  const tiles: Tile[] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const tile = grid[row][col];
      if (tile) {
        tiles.push(tile);
      }
    }
  }
  return tiles;
}
